// Deep workspace tabs (Phase 4): social activity with coverage, page changes with before/after,
// positioning analysis by a model (claims quote their source), comparison with the Mirage profile.
import { useState } from 'react';
import { Link } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, Section, StatusMark, Time, Unknown } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { Activity, Analyses, AnalysisRow, ChannelMetric, Claim, Comparison, PageChange, PostType, SocialAnalysisRow } from '../../lib/deepTypes';
import { jobType } from '../../lib/format';
import { useApi } from '../../lib/session';
import type { Evidence, Job, StudioDetail } from '../../lib/types';
import { CollectOutputs } from './OtherTabs';
import { accessLabel } from './OverviewTab';
import { HistorySection } from './History';
import { InstagramSection } from './InstagramSection';
import { AllPosts, ManualPostForm, TimelineChart } from './SocialPosts';

const STATE_LABEL: Record<string, string> = {
  complete: 'read',
  partial: 'read, gaps',
  blocked: 'blocked',
  failed: 'failed',
  unavailable: 'unavailable',
  not_permitted: 'not permitted',
  no_adapter: 'no adapter',
};
const STATE_TONE: Record<string, string> = { complete: 'ok', partial: 'warn', blocked: 'bad', failed: 'bad', not_permitted: 'bad' };

function MetricRow({ m, onCollect, busy }: { m: ChannelMetric; onCollect?: () => void; busy: boolean }) {
  return (
    <tr>
      <td>
        {m.channel}
        {m.watched === false && <span className="muted small"> · not watched</span>}
      </td>
      <td>{m.last_state ? <Badge tone={STATE_TONE[m.last_state]}>{STATE_LABEL[m.last_state] ?? m.last_state}</Badge> : <Unknown>not collected yet</Unknown>}</td>
      <td className="num">{m.posts_observed ?? <Unknown>not measured</Unknown>}</td>
      <td className="num">{m.posts_per_week ?? <Unknown>{m.posts_observed === null ? 'not measured' : 'partial window'}</Unknown>}</td>
      <td className="small">
        {m.label}
        {m.last_note && !m.label?.includes(m.last_note) && <div className="muted">{m.last_note}</div>}
      </td>
      <td className="small">{m.last_observed_at ? <Time iso={m.last_observed_at} /> : '—'}</td>
      <td>
        {onCollect && (
          <button type="button" className="btn small" disabled={busy} onClick={onCollect}>
            Collect now
          </button>
        )}
      </td>
    </tr>
  );
}

const POST_TYPE_LABEL: Record<PostType, string> = {
  project_showcase: 'Project promotion',
  technical: 'Technical / making-of',
  internal_tool: 'Own tool or product',
  company_culture: 'Company culture / team',
  hiring: 'Hiring',
  news: 'News / awards / announcements',
  other: 'Other',
};

function SocialAnalysisView({ a, onReview, canReview }: { a: SocialAnalysisRow; onReview: (s: string) => void; canReview: boolean }) {
  const r = a.result;
  const types = (Object.keys(POST_TYPE_LABEL) as PostType[]).filter((t) => r.mix[t] > 0);
  const channels = Object.entries(r.channels)
    .map(([c, n]) => `${c} ${n}`)
    .join(', ');
  return (
    <div className="stack-s" data-testid="social-analysis">
      <p className="small muted">
        Analysis of <Time iso={a.created_at} /> · {a.status} · {a.model.provider} {a.model.name} · {a.model.is_local ? 'local' : 'cloud'} · {a.prompt_version}
      </p>
      <p className="small">
        {r.posts_labelled} of {r.posts_given} posts labelled with a supporting quote ({channels})
        {r.window.first && (
          <>
            , published <Time iso={r.window.first} /> → <Time iso={r.window.last} />
          </>
        )}
        .
      </p>
      {types.length === 0 ? (
        <Empty>No post could be labelled with a quote from its own text.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table compact">
            <thead>
              <tr>
                <th>Post type</th>
                <th className="num">Posts</th>
                <th className="num">Share of labelled</th>
                <th>Example</th>
              </tr>
            </thead>
            <tbody>
              {types.map((t) => {
                const ex = r.labels.find((l) => l.type === t);
                return (
                  <tr key={t}>
                    <td>{POST_TYPE_LABEL[t]}</td>
                    <td className="num">{r.mix[t]}</td>
                    <td className="num">{Math.round((100 * r.mix[t]) / r.posts_labelled)}%</td>
                    <td className="small">
                      {ex &&
                        (ex.url ? (
                          <a href={ex.url} target="_blank" rel="noreferrer noopener">
                            {ex.title}
                          </a>
                        ) : (
                          ex.title
                        ))}
                      {ex && <span className="muted"> · “{ex.quote}”</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(r.tone.length > 0 || r.interpretation.summary) && (
        <div className="hypothesis">
          <h3 className="sub">Tone, as the model reads it (interpretation, not evidence)</h3>
          {r.tone.length > 0 && (
            <ul className="plain small">
              {r.tone.map((t, i) => (
                <li key={i}>
                  <strong>{t.trait}</strong> <span className="muted">— “{t.quote}”</span>
                </li>
              ))}
            </ul>
          )}
          {r.interpretation.summary && <p className="small">{r.interpretation.summary}</p>}
        </div>
      )}
      {a.unverified.length > 0 && (
        <details className="small">
          <summary>{a.unverified.length} label(s) dropped: their quote was not found in the cited post</summary>
          <ul className="plain">
            {a.unverified.map((u, i) => (
              <li key={i}>
                {u.type ?? u.trait}: “{u.quote}” <span className="muted">({u.reason})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {canReview && (
        <div className="row gap">
          <button type="button" className="btn small" disabled={a.status === 'accepted'} onClick={() => onReview('accepted')}>
            Accept
          </button>
          <button type="button" className="btn small" disabled={a.status === 'rejected'} onClick={() => onReview('rejected')}>
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

function SocialContentSection({ studio, canReview, postCount }: { studio: StudioDetail; canReview: boolean; postCount: number }) {
  const an = useApi<Analyses<SocialAnalysisRow>>(`/api/studios/${studio.id}/analyses?kind=social_content`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');

  async function request() {
    setBusy(true);
    setErr('');
    try {
      const r = await api<{ job_id: string; created: boolean }>(`/api/studios/${studio.id}/analyses?kind=social_content`, { method: 'POST', idempotencyKey: newIdempotencyKey() });
      setMsg(r.created ? 'Analysis queued for the model agent.' : 'An analysis is already queued or running.');
      an.reload();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function review(id: string, status: string) {
    try {
      await api(`/api/analyses/${id}/review`, { body: { status } });
      an.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  const latest = an.data?.items[0];
  return (
    <Section
      title="Post types and tone"
      aside={
        <button type="button" className="btn small" disabled={busy || postCount === 0 || !an.data?.model_jobs_enabled || !!an.data?.active_job_id} onClick={() => void request()}>
          Analyse posts with the model
        </button>
      }
    >
      {an.data && !an.data.model_jobs_enabled && <p className="msg info small">{an.data.model_jobs_reason}</p>}
      {postCount === 0 && <p className="small muted">No post yet: collect a blog, Vimeo or YouTube channel (Deep studios), or enter a post by hand above.</p>}
      {an.data?.active_job_id && (
        <p className="small">
          Waiting for the model agent: <Link to={`/jobs/${an.data.active_job_id}`}>job {an.data.active_job_id.slice(0, 8)}</Link>
        </p>
      )}
      {msg && <p className="msg ok small">{msg}</p>}
      <ErrorMsg>{err || an.error}</ErrorMsg>
      {latest ? <SocialAnalysisView a={latest} canReview={canReview} onReview={(s) => void review(latest.id, s)} /> : an.data && <Empty>No analysis yet.</Empty>}
      <p className="muted small">
        A model labels each post (project promotion, technical, own tool, company culture, hiring, news, other) and must quote the post for
        every label. The server keeps a label only if the quote is really in that post, and counts the mix itself. It reads the posts this app collected
        from channels it may read, and the posts you entered by hand (marked as such). Platforms marked “not permitted” are never collected.
      </p>
    </Section>
  );
}

export function SocialTab({ studio, canReview = false }: { studio: StudioDetail; canReview?: boolean }) {
  const [days, setDays] = useState(90);
  const act = useApi<Activity>(`/api/studios/${studio.id}/activity?window_days=${days}`);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const supported = ['blog', 'vimeo', 'youtube'];

  async function collect(channel: string) {
    setBusy(channel);
    setErr('');
    try {
      const r = await api<{ job_id: string }>(`/api/studios/${studio.id}/collect`, { body: { channel }, idempotencyKey: newIdempotencyKey() });
      setMsg(`Collection of ${channel} queued (job ${r.job_id.slice(0, 8)}). Reload this tab when it has run.`);
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy('');
    }
  }

  const deep = studio.research_depth === 'deep';
  const [version, setVersion] = useState(0);
  const changed = (m: string) => {
    setMsg(m);
    setVersion((v) => v + 1);
    act.reload();
  };
  return (
    <div>
      <Section
        title="Posting activity"
        aside={
          <label className="small">
            Window{' '}
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label="Window">
              {[30, 90, 180, 365].map((d) => (
                <option key={d} value={d}>
                  {d} days
                </option>
              ))}
            </select>
          </label>
        }
      >
        {act.loading && !act.data ? (
          <Loading what="Loading activity" />
        ) : !act.data ? (
          <ErrorMsg>{act.error}</ErrorMsg>
        ) : act.data.metrics.channels.length === 0 ? (
          <Empty>{deep ? 'No social channel is watched. Choose channels in Deep settings (Overview tab).' : 'Feeds and channels are collected for Deep studios only. A Light studio can still have posts entered by hand, a timeline and an analysis (below).'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Last run</th>
                  <th className="num">Posts observed</th>
                  <th className="num">Posts / week</th>
                  <th>What this covers</th>
                  <th>Observed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {act.data.metrics.channels.map((m) => (
                  <MetricRow
                    key={m.channel}
                    m={m}
                    busy={busy === m.channel}
                    onCollect={deep && !studio.is_demo && m.watched && supported.includes(m.channel) ? () => void collect(m.channel) : undefined}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {act.data && <p className="muted small">{act.data.assumption} Platforms are never compared with each other, and likes say nothing about sales.</p>}
        {msg && <p className="msg ok small">{msg}</p>}
        <ErrorMsg>{err}</ErrorMsg>
      </Section>
      {!studio.is_demo && <HistorySection studio={studio} onDone={() => changed('Look-back finished.')} />}
      {!studio.is_demo && <InstagramSection studio={studio} onDone={() => changed('Instagram read finished.')} />}
      {!studio.is_demo && (
        <Section title="Add a post by hand">
          <p className="muted small">
            For posts the app cannot collect (LinkedIn, Instagram, Facebook, X…). Paste the text as published; it is stored in full, counted on the timeline and included in
            the analysis, marked “{'entered by hand'}”. Not part of the posting-rate figures.
          </p>
          <ManualPostForm studio={studio} onSaved={changed} />
        </Section>
      )}
      <TimelineChart studioId={studio.id} version={version} />
      <SocialContentSection studio={studio} canReview={canReview} postCount={act.data?.posts.length ?? 0} />
      <Section title="Accounts">
        {studio.socials.length === 0 ? (
          <Empty>No social account recorded. Add one on the Overview tab.</Empty>
        ) : (
          <ul className="plain">
            {studio.socials.map((s) => (
              <li key={s.id}>
                {s.platform}:{' '}
                <a href={s.url} target="_blank" rel="noreferrer noopener">
                  {s.handle ?? s.url}
                </a>{' '}
                <span className="muted small">· {act.data?.not_permitted[s.platform] ? `not permitted: ${act.data.not_permitted[s.platform]}` : accessLabel(s.access_status)}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <AllPosts studioId={studio.id} version={version} onChanged={changed} />
      {act.data && act.data.coverage.length > 0 && (
        <Section title="Coverage history">
          <ul className="plain small">
            {act.data.coverage.map((c) => (
              <li key={c.id}>
                <Time iso={c.observed_at} /> · {c.channel} · <Badge tone={STATE_TONE[c.state]}>{STATE_LABEL[c.state] ?? c.state}</Badge>{' '}
                {c.window_start ? (
                  <>
                    window <Time iso={c.window_start} /> → <Time iso={c.window_end} />, {c.items_seen} items
                  </>
                ) : (
                  <span className="muted">no window</span>
                )}
                {c.note && <span className="muted"> · {c.note}</span>}
                {c.job_id && (
                  <>
                    {' '}
                    · <Link to={`/jobs/${c.job_id}`}>job</Link>
                  </>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

function Diff({ c }: { c: PageChange }) {
  const meta = ['title', 'h1', 'description'].filter((k) => (c.meta_before?.[k] ?? '') !== (c.meta_after?.[k] ?? ''));
  return (
    <div className="diff">
      {meta.map((k) => (
        <p key={k} className="small">
          <strong>{k}</strong>: <del>{c.meta_before?.[k] || '(none)'}</del> → <ins>{c.meta_after?.[k] || '(none)'}</ins>
        </p>
      ))}
      {c.added.length > 0 && (
        <ul className="diff-lines add">
          {c.added.slice(0, 20).map((s, i) => (
            <li key={i}>+ {s}</li>
          ))}
          {c.added.length > 20 && <li className="muted">… {c.added.length - 20} more</li>}
        </ul>
      )}
      {c.removed.length > 0 && (
        <ul className="diff-lines del">
          {c.removed.slice(0, 20).map((s, i) => (
            <li key={i}>− {s}</li>
          ))}
          {c.removed.length > 20 && <li className="muted">… {c.removed.length - 20} more</li>}
        </ul>
      )}
    </div>
  );
}

export function ChangesTab({ studio, jobs }: { studio: StudioDetail; jobs: Job[] | null }) {
  const ch = useApi<{ items: PageChange[]; note: string }>(`/api/studios/${studio.id}/changes`);
  const [showDismissed, setShowDismissed] = useState(false);
  const [err, setErr] = useState('');

  async function mark(id: string, state: string) {
    setErr('');
    try {
      await api(`/api/changes/${id}/review`, { body: { state } });
      ch.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  const items = (ch.data?.items ?? []).filter((c) => showDismissed || c.review_state !== 'dismissed');
  const runs = (jobs ?? []).filter((j) => j.type === 'collect_deep_sources' || j.type === 'refresh_light_profile');
  return (
    <div>
      <Section
        title="Material changes"
        aside={
          <label className="check small">
            <input type="checkbox" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} /> show dismissed
          </label>
        }
      >
        <ErrorMsg>{err || ch.error}</ErrorMsg>
        {ch.loading && !ch.data ? (
          <Loading what="Loading changes" />
        ) : items.length === 0 ? (
          <Empty>No material change detected. A change needs two fetched versions of the same page.</Empty>
        ) : (
          <ul className="runs">
            {items.map((c) => (
              <li key={c.id} className="run">
                <div className="row gap center wrap">
                  {c.categories.map((k) => (
                    <Badge key={k}>{k}</Badge>
                  ))}
                  <a href={c.url} target="_blank" rel="noreferrer noopener" className="small">
                    {c.url}
                  </a>
                  <span className="muted small">
                    <Time iso={c.before.fetched_at} /> → <Time iso={c.after.fetched_at} />
                  </span>
                  {c.review_state !== 'new' && <span className="muted small">· {c.review_state}</span>}
                </div>
                <Diff c={c} />
                {c.hypothesis && <p className="small hypothesis">{c.hypothesis}</p>}
                <div className="row gap">
                  {c.review_state !== 'seen' && (
                    <button type="button" className="btn small" onClick={() => void mark(c.id, 'seen')}>
                      Mark seen
                    </button>
                  )}
                  {c.review_state !== 'dismissed' ? (
                    <button type="button" className="btn small" onClick={() => void mark(c.id, 'dismissed')}>
                      Dismiss (noise)
                    </button>
                  ) : (
                    <button type="button" className="btn small" onClick={() => void mark(c.id, 'new')}>
                      Restore
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        {ch.data && <p className="muted small">{ch.data.note} Both versions stay in Sources.</p>}
      </Section>
      <Section title="Website collection runs">
        {runs.length === 0 ? (
          <Empty>No website collection has run for this studio yet.</Empty>
        ) : (
          <ul className="runs">
            {runs.map((j) => (
              <li key={j.id} className="run">
                <div>
                  <StatusMark status={j.status} /> <Link to={`/jobs/${j.id}`}>{jobType(j.type)}</Link> · <Time iso={j.finished_at ?? j.created_at} />
                </div>
                {j.error && <p className="small err-text">{j.error}</p>}
                <CollectOutputs out={j.outputs} />
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function ClaimList({ title, claims }: { title: string; claims: Claim[] }) {
  if (!claims.length) return null;
  return (
    <>
      <h3 className="sub">{title}</h3>
      <ul className="facts">
        {claims.map((c, i) => (
          <li key={i} className="fact">
            <div className="fact-value">{c.text}</div>
            <blockquote className="excerpt">“{c.quote}”</blockquote>
            <p className="small muted">
              quoted from{' '}
              {c.url ? (
                <a href={c.url} target="_blank" rel="noreferrer noopener">
                  {c.url}
                </a>
              ) : (
                'a page'
              )}
            </p>
          </li>
        ))}
      </ul>
    </>
  );
}

function AnalysisView({ a, onReview, canReview }: { a: AnalysisRow; onReview: (s: string) => void; canReview: boolean }) {
  const r = a.result;
  const empty = !r.headline && ![r.promises, r.target_sectors, r.differentiation, r.calls_to_action, r.new_offers].some((x) => x.length);
  return (
    <Section
      title={
        <>
          Analysis of <Time iso={a.created_at} /> <span className="muted small">· {a.status}</span>
        </>
      }
      aside={
        <span className="small muted">
          {a.model.provider} {a.model.name} · {a.model.is_local ? 'local' : 'cloud'} · {a.prompt_version}
        </span>
      }
    >
      {empty && <Empty>The model found nothing it could support with a quote from the pages.</Empty>}
      {r.headline && <ClaimList title="Headline" claims={[r.headline]} />}
      <ClaimList title="Promises" claims={r.promises} />
      <ClaimList title="Target sectors" claims={r.target_sectors} />
      <ClaimList title="Differentiation" claims={r.differentiation} />
      <ClaimList title="Calls to action" claims={r.calls_to_action} />
      <ClaimList title="New offers" claims={r.new_offers} />
      {(r.interpretation.tone || r.interpretation.summary) && (
        <div className="hypothesis">
          <h3 className="sub">Model interpretation (not evidence)</h3>
          {r.interpretation.tone && <p className="small">Tone: {r.interpretation.tone}</p>}
          {r.interpretation.summary && <p className="small">{r.interpretation.summary}</p>}
        </div>
      )}
      {a.unverified.length > 0 && (
        <details className="small">
          <summary>{a.unverified.length} claim(s) dropped: their quote was not found in the cited page</summary>
          <ul className="plain">
            {a.unverified.map((c, i) => (
              <li key={i}>
                {c.field}: {c.text} — “{c.quote}” <span className="muted">({c.reason})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {canReview && (
        <div className="row gap">
          <button type="button" className="btn small" disabled={a.status === 'accepted'} onClick={() => onReview('accepted')}>
            Accept
          </button>
          <button type="button" className="btn small" disabled={a.status === 'rejected'} onClick={() => onReview('rejected')}>
            Reject
          </button>
        </div>
      )}
    </Section>
  );
}

function CompareBlock({ label, c }: { label: string; c: Comparison['services'] }) {
  if (c.state !== 'compared') {
    const why = c.state === 'mirage_profile_empty' ? 'Mirage profile has none yet (Settings)' : 'unknown for this studio';
    return (
      <p className="small">
        {label}: <Unknown>{why}</Unknown>
      </p>
    );
  }
  return (
    <p className="small">
      {label}: both <strong>{c.both.join(', ') || 'none'}</strong> · only Mirage {c.only_mirage.join(', ') || 'none'} · only them{' '}
      {c.only_studio.join(', ') || 'none'}
    </p>
  );
}

export function PositioningTab({ studio, evidence, canReview }: { studio: StudioDetail; evidence: Evidence | null; canReview: boolean }) {
  const an = useApi<Analyses>(`/api/studios/${studio.id}/analyses`);
  const cmp = useApi<Comparison>(`/api/studios/${studio.id}/comparison`);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const src = evidence?.sources.find((s) => s.id === studio.summary_source_id);

  async function request() {
    setBusy(true);
    setErr('');
    try {
      const r = await api<{ job_id: string; created: boolean }>(`/api/studios/${studio.id}/analyses`, { method: 'POST', idempotencyKey: newIdempotencyKey() });
      setMsg(r.created ? 'Analysis queued for the model agent.' : 'An analysis is already queued or running.');
      an.reload();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  async function review(id: string, status: string) {
    try {
      await api(`/api/analyses/${id}/review`, { body: { status } });
      an.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  return (
    <div className="narrow-col">
      <Section title="Summary (the studio’s own words)">
        {studio.summary ? <p className="lede">{studio.summary}</p> : <Empty>No summary yet.</Empty>}
        <p className="small muted">
          Source:{' '}
          {src?.url ? (
            <a href={src.url} target="_blank" rel="noreferrer noopener">
              {src.url}
            </a>
          ) : (
            'not recorded'
          )}
        </p>
      </Section>
      <Section title="Compared with Mirage">
        {cmp.data ? (
          <>
            <CompareBlock label="Services" c={cmp.data.services} />
            <CompareBlock label="Client types" c={cmp.data.client_types} />
            <CompareBlock label="Markets" c={cmp.data.markets} />
            <p className="muted small">{cmp.data.note}</p>
          </>
        ) : (
          <ErrorMsg>{cmp.error}</ErrorMsg>
        )}
      </Section>
      <Section
        title="Positioning analysis"
        aside={
          studio.research_depth === 'deep' && (
            <button type="button" className="btn small" disabled={busy || !an.data?.model_jobs_enabled || !!an.data?.active_job_id} onClick={() => void request()}>
              Analyse with the model
            </button>
          )
        }
      >
        {an.data && !an.data.model_jobs_enabled && <p className="msg info small">{an.data.model_jobs_reason}</p>}
        {an.data?.active_job_id && (
          <p className="small">
            Waiting for the model agent: <Link to={`/jobs/${an.data.active_job_id}`}>job {an.data.active_job_id.slice(0, 8)}</Link>
          </p>
        )}
        {msg && <p className="msg ok small">{msg}</p>}
        <ErrorMsg>{err || an.error}</ErrorMsg>
        <p className="muted small">
          A model reads the latest fetched pages and must quote them for every claim. The server keeps a claim only if its quote is really on the
          cited page; tone and summary are interpretation. Nothing here changes the studio record.
        </p>
      </Section>
      {(an.data?.items ?? []).map((a) => (
        <AnalysisView key={a.id} a={a} canReview={canReview} onReview={(s) => void review(a.id, s)} />
      ))}
    </div>
  );
}
